// Displays a leaderboard

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { useRoomPlayerLookup, useRoomPlayerSelection } from '../../hooks';
import { resolvePlayerPalette } from '../../styles/playerColors';

type ColorMode = 'playerColors' | 'grayScale';
interface PlayingLeaderboardProps {
  scoreType: 'turn' | 'game';
  myColors?: ColorMode;
  othersColors?: ColorMode;
  /**
   * Optional game-specific glyph rendered immediately after each player's name
   * (mirrors the host GameLeaderboard `playerNameIcon`). Keyed by playerId;
   * players without an entry render no icon.
   */
  playerIcons?: Record<string, { icon: LucideIcon; label: string }>;
}

const GRAY_SCALE_COLORS = {
  rowBg: 'var(--muted)',
  scoreBg: 'var(--foreground)',
  textColor: 'var(--foreground)',
} as const;

const PlayingLeaderboard: React.FC<PlayingLeaderboardProps> = ({
  scoreType,
  myColors = 'playerColors',
  othersColors = 'playerColors',
  playerIcons,
}) => {
  const { selectPlayerData } = useRoomPlayerSelection();
  const { isMyPlayer } = useRoomPlayerLookup();

  const getRowColors = (player: { playerColor?: string }, isMe: boolean) => {
    const colorMode = isMe ? myColors : othersColors;

    if (colorMode === 'grayScale') {
      return GRAY_SCALE_COLORS;
    }

    const { palette } = resolvePlayerPalette(player);
    return {
      rowBg: palette.dark,
      scoreBg: palette.base,
      textColor: palette.light,
    };
  };

  const sortedPlayers = selectPlayerData({
    sortField: scoreType === 'turn' ? 'scoreTurn' : 'scoreGame',
    sort: 'desc',
  });

  return (
    // The radix viewport wraps content in a `display:table` div that grows to
    // fit nowrap names (long names then overflow past the right gutter); force
    // it back to a block so rows stay bounded to 100% and truncate.
    <ScrollArea className="mx-auto my-10 h-[calc(100vh-80px)] w-[88vw] max-w-[640px] [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div className="flex w-full flex-col items-center gap-2 p-4">
        {sortedPlayers.map((player) => {
          const score =
            scoreType === 'turn'
              ? player.scoreTurn || 0
              : player.scoreGame || 0;
          const isMe = isMyPlayer(player.playerId);
          const { rowBg, scoreBg, textColor } = getRowColors(player, isMe);
          const playerIcon = playerIcons?.[player.playerId];
          const PlayerNameIcon = playerIcon?.icon;

          return (
            <div
              key={player.playerId}
              className="flex w-full shrink-0 items-center justify-between gap-6 min-h-14 rounded-lg px-6 py-4"
              style={{ background: rowBg }}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className="min-w-0 text-xl font-bold leading-tight truncate"
                  style={{ color: textColor }}
                >
                  {player.playerName}
                </span>
                {PlayerNameIcon && (
                  <PlayerNameIcon
                    aria-label={playerIcon?.label}
                    role="img"
                    className="size-6 shrink-0"
                    style={{ color: textColor }}
                  />
                )}
              </div>
              <div className="relative flex items-center justify-center min-w-[72px] h-12 rounded-lg shrink-0">
                <div
                  className="absolute inset-0 rounded-lg opacity-[0.16]"
                  style={{ background: scoreBg }}
                />
                <span
                  className="relative z-10 text-xl font-bold text-center"
                  style={{ color: textColor }}
                >
                  {score}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export default PlayingLeaderboard;
